import type { DateRangeSelection, ResolvedDateRange } from '../filters/filter-state.model';

/**
 * UTC calendar-day range resolution for the global filter bar (AITH-44, R-010).
 *
 * Every preset and custom range resolves to a **half-open `[fromUtc, toUtc)`**
 * window on UTC midnights, which is exactly what `ClientUsageWindow` and
 * `/dashboard/usage` document — so no consumer has to adjust an inclusive end
 * date and risk a double-counted or dropped day.
 *
 * "Now" is always supplied by the caller (from `ClockService`), never read from
 * `Date.now()` here: every preset-boundary assertion needs a fixed instant.
 *
 * This lives beside `ClockService` rather than widening
 * `live-tiles/live-tile-snapshot.validator.ts` — those `isUtcDay`/`isUtcInstant`
 * helpers are *validators* for the tile schema, not range arithmetic (R-010).
 */

const MS_PER_DAY = 86_400_000;
const UTC_DAY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/** True for a well-formed, existent UTC calendar day (`YYYY-MM-DD`). */
export function isValidUtcDay(value: string): boolean {
  if (!UTC_DAY_PATTERN.test(value)) return false;
  const parsed = Date.parse(`${value}T00:00:00.000Z`);
  if (Number.isNaN(parsed)) return false;
  // Rejects real-looking but non-existent days (2026-02-30, 2027-02-29): the
  // round trip only matches when the calendar actually has that day.
  return utcDayOf(parsed) === value;
}

/** The UTC calendar day containing an instant — never the viewer's local day (SC-002). */
export function utcDayOf(epochMs: number): string {
  const date = new Date(epochMs);
  const year = String(date.getUTCFullYear()).padStart(4, '0');
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/** Epoch milliseconds at the start (00:00:00.000Z) of a UTC calendar day. */
export function utcDayStartMs(utcDay: string): number {
  return Date.parse(`${utcDay}T00:00:00.000Z`);
}

/** The UTC midnight instant that opens a UTC calendar day, as an ISO-8601 `Z` string. */
export function utcMidnightOf(utcDay: string): string {
  return new Date(utcDayStartMs(utcDay)).toISOString();
}

/**
 * The exclusive `toUtc` for an **inclusive** last UTC day — the next midnight.
 * The URL carries the inclusive day because it is human-facing; the API window
 * stays half-open (filter-url contract, "Frozen surface").
 */
export function toUtcExclusiveOf(inclusiveEndUtcDay: string): string {
  return new Date(utcDayStartMs(inclusiveEndUtcDay) + MS_PER_DAY).toISOString();
}

/** The inverse of {@link toUtcExclusiveOf}: the inclusive last UTC day of a half-open window. */
export function inclusiveEndDayOf(toUtc: string): string {
  return utcDayOf(Date.parse(toUtc) - MS_PER_DAY);
}

/**
 * True when the selection can be resolved. Callers validate *before* writing to
 * the shared state so an inverted custom range never becomes the active
 * selection (data-model §1: the previous valid range stays active).
 */
export function isValidDateRangeSelection(selection: DateRangeSelection): boolean {
  if (selection.kind === 'preset') return true;
  return (
    isValidUtcDay(selection.startUtcDay)
    && isValidUtcDay(selection.endUtcDay)
    // Lexicographic comparison is exact for zero-padded ISO calendar days.
    && selection.startUtcDay <= selection.endUtcDay
  );
}

/**
 * Resolves a selection against a fixed instant.
 *
 * Presets are relative and re-resolve as the UTC day advances (R-018); a custom
 * range is absolute and ignores `nowMs` entirely.
 *
 * @throws RangeError when the selection is invalid — guard with
 * {@link isValidDateRangeSelection} at every entry point, so this only ever
 * fires on a programming error rather than on user input.
 */
export function resolveDateRange(
  selection: DateRangeSelection,
  nowMs: number,
): ResolvedDateRange {
  if (!isValidDateRangeSelection(selection)) {
    throw new RangeError('Invalid date-range selection: start must be a UTC day on or before end.');
  }

  if (selection.kind === 'custom') {
    return Object.freeze({
      fromUtc: utcMidnightOf(selection.startUtcDay),
      toUtc: toUtcExclusiveOf(selection.endUtcDay),
    });
  }

  // The current UTC day is always included, so every preset's exclusive bound
  // is the next UTC midnight.
  const todayStartMs = utcDayStartMs(utcDayOf(nowMs));
  const toUtc = new Date(todayStartMs + MS_PER_DAY).toISOString();
  const today = new Date(todayStartMs);

  const fromMs = ((): number => {
    switch (selection.preset) {
      case 'today':
        return todayStartMs;
      case 'last7':
        return todayStartMs - 6 * MS_PER_DAY;
      case 'last30':
        return todayStartMs - 29 * MS_PER_DAY;
      case 'last90':
        return todayStartMs - 89 * MS_PER_DAY;
      case 'mtd':
        return Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), 1);
      case 'qtd':
        return Date.UTC(today.getUTCFullYear(), Math.floor(today.getUTCMonth() / 3) * 3, 1);
      case 'ytd':
        return Date.UTC(today.getUTCFullYear(), 0, 1);
    }
  })();

  return Object.freeze({ fromUtc: new Date(fromMs).toISOString(), toUtc });
}

/**
 * The R-002 option-list probe window: a trailing `days`-day half-open UTC
 * window ending at the next UTC midnight. Computed once per session and held —
 * deliberately independent of the user's selected range (FR-015).
 */
export function trailingUtcWindow(nowMs: number, days: number): ResolvedDateRange {
  const todayStartMs = utcDayStartMs(utcDayOf(nowMs));
  return Object.freeze({
    fromUtc: new Date(todayStartMs - (days - 1) * MS_PER_DAY).toISOString(),
    toUtc: new Date(todayStartMs + MS_PER_DAY).toISOString(),
  });
}
